import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, AlertCircle } from "lucide-react";
import { usePerkPromoCodes, useUploadPromoCodes } from "@/hooks/usePerks";
import { format } from "date-fns";
import type { PromoCodeStatus } from "@/types/generated/cms.generated";

interface PromoCodeManagerProps {
  perkId: string;
}

const statusVariant: Record<PromoCodeStatus, "default" | "secondary"> = {
  Available: "default",
  Assigned: "secondary",
};

export function PromoCodeManager({ perkId }: PromoCodeManagerProps) {
  const { data: promoCodes = [], isLoading, error } = usePerkPromoCodes(perkId);
  const uploadCodes = useUploadPromoCodes();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [codesText, setCodesText] = useState("");
  const [reactivatePerk, setReactivatePerk] = useState(false);

  const handleUpload = () => {
    const codes = codesText
      .split("\n")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    if (codes.length === 0) return;

    uploadCodes.mutate(
      { perkId, data: { codes, reactivatePerk } },
      {
        onSuccess: () => {
          setUploadDialogOpen(false);
          setCodesText("");
          setReactivatePerk(false);
        },
      }
    );
  };

  const availableCount = promoCodes.filter((c) => c.status === "Available").length;
  const assignedCount = promoCodes.filter((c) => c.status === "Assigned").length;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error loading promo codes</AlertTitle>
        <AlertDescription>Failed to load promo codes. Please try refreshing.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          <Badge variant="outline">Total: {promoCodes.length}</Badge>
          <Badge variant="default">Available: {availableCount}</Badge>
          <Badge variant="secondary">Assigned: {assignedCount}</Badge>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Upload Codes
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No promo codes uploaded yet. Upload codes to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned At</TableHead>
                  <TableHead>Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono text-sm">{code.code}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[code.status || "Available"]}>
                        {code.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {code.assignedAt ? format(new Date(code.assignedAt), "MMM d, yyyy HH:mm") : "-"}
                    </TableCell>
                    <TableCell>
                      {code.createdAt ? format(new Date(code.createdAt), "MMM d, yyyy HH:mm") : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Promo Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Promo Codes (one per line)</Label>
              <Textarea
                placeholder={"CODE-001\nCODE-002\nCODE-003"}
                className="min-h-[150px] font-mono text-sm mt-1"
                value={codesText}
                onChange={(e) => setCodesText(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {codesText.split("\n").filter((c) => c.trim()).length} codes entered
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={reactivatePerk}
                onCheckedChange={setReactivatePerk}
                id="reactivate"
              />
              <Label htmlFor="reactivate">Reactivate perk if sold out</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploadCodes.isPending || !codesText.trim()}
            >
              {uploadCodes.isPending ? "Uploading..." : "Upload Codes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
